import { useCallback, useEffect, useState } from "react";
import { useHub } from "@/components/AppShell";
import { Copy, KeyRound, Link2, Loader2, Plus, Share2, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { Field, StatusPill } from "@/components/common";

const STATUS_RGB = { Active: "var(--st-emerald)", Expired: "var(--st-slate)", Revoked: "var(--st-red)" };
const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const copy = (t, label) => navigator.clipboard?.writeText(t).then(() => toast.success(`${label} copied`)).catch(() => toast.error("Couldn't copy"));

function Secret({ shown, onClose }) {
  const share = () => (navigator.share ? navigator.share({ title: shown.name, text: `Code: ${shown.code}`, url: shown.url }).catch(() => {}) : copy(`${shown.url}\nCode: ${shown.code}`, "Link and code"));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent bigClose className="sm:max-w-md" data-testid="sitter-secret">
        <DialogHeader><DialogTitle className="font-display">{shown.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-q-muted">Shown once. Send the link and the code to your sitter.</p>
        {shown.url && <p className="break-all rounded-xl bg-q-raised p-3 text-xs text-q-text" data-testid="sitter-secret-url">{shown.url}</p>}
        <p className="text-center font-display text-4xl font-extrabold tracking-[0.3em] text-q-text" data-testid="sitter-secret-code">{shown.code}</p>
        <div className="grid grid-cols-3 gap-2">
          {shown.url && <button className="q-btn q-btn-secondary" onClick={() => copy(shown.url, "Link")} data-testid="sitter-copy-link"><Copy className="h-4 w-4" /> Link</button>}
          <button className="q-btn q-btn-secondary" onClick={() => copy(shown.code, "Code")} data-testid="sitter-copy-code"><Copy className="h-4 w-4" /> Code</button>
          {shown.url && <button className="q-btn q-btn-primary" onClick={share} data-testid="sitter-share"><Share2 className="h-4 w-4" /> Share</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const INC = [["contacts", "Family contacts"], ["house_info", "House info (Household manual)", "manual"], ["shutoffs", "Shutoff cards", "shutoffs"], ["away", "Away checklist", "away"], ["card", "Printable emergency card"]];

function NewLink({ hubId, kids, durations, types, pets = [], onClose, onMade }) {
  const [name, setName] = useState("");
  const [ids, setIds] = useState([]);
  const [hours, setHours] = useState(24);
  const [type, setType] = useState("Babysitter");
  const [inc, setInc] = useState({ contacts: true, house_info: false, card: false, pet_card: false, pet_ids: [], shutoffs: false, away: false, care_ids: [] });
  const [care, setCare] = useState([]);
  useEffect(() => { api.get(`/organizations/${hubId}/care`).then(({ data }) => setCare(data.profiles || [])).catch(() => setCare([])); }, [hubId]);
  const { hub } = useHub();
  const off = hub?.sections_off || [];
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/organizations/${hubId}/emergency/guest-passes`, { name, kid_ids: ids, hours, type, include: inc }); onMade({ name: data.link.name, url: data.url, code: data.code }); } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const any = ids.length || inc.contacts || inc.house_info || inc.card || inc.pet_card || inc.shutoffs || inc.away || inc.care_ids.length;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent bigClose className="max-h-[92vh] overflow-y-auto sm:max-w-md" data-testid="sitter-new">
        <DialogHeader><DialogTitle className="font-display">New guest pass</DialogTitle></DialogHeader>
        <Field label="Name"><input className="q-input w-full" maxLength={40} value={name} placeholder="Saturday sitter" onChange={(e) => setName(e.target.value)} data-testid="sitter-name-input" /></Field>
        <Field label="Type"><select className="q-input min-h-[44px] w-full" value={type} onChange={(e) => setType(e.target.value)} data-testid="guest-type-select">{types.map((x) => <option key={x}>{x}</option>)}</select></Field>
        <div>
          <p className="mb-1 text-xs font-bold text-q-muted">Kids' emergency cards and sitter notes</p>
          {!kids.length && <p className="text-sm text-q-faint">No kids in this hub.</p>}
          {kids.map((k) => (
            <label key={k.key} className="flex min-h-[44px] items-center gap-3">
              <Checkbox className="h-6 w-6" checked={ids.includes(k.key.slice(4))} onCheckedChange={(v) => setIds((x) => (v ? [...x, k.key.slice(4)] : x.filter((i) => i !== k.key.slice(4))))} data-testid={`sitter-kid-${k.name}`} /> {k.name}
            </label>
          ))}
        </div>
        <div data-testid="guest-include">
          <p className="mb-1 text-xs font-bold text-q-muted">Also include</p>
          {INC.filter(([, , s]) => !s || !off.includes(s)).map(([k, l]) => (
            <label key={k} className="flex min-h-[44px] items-center gap-3">
              <Checkbox className="h-6 w-6" checked={inc[k]} onCheckedChange={(v) => setInc({ ...inc, [k]: !!v })} data-testid={`guest-inc-${k}`} /> {l}
            </label>
          ))}
          {pets.length > 0 && <label className="flex min-h-[44px] items-center gap-3">
            <Checkbox className="h-6 w-6" checked={inc.pet_card} onCheckedChange={(v) => setInc({ ...inc, pet_card: !!v })} data-testid="guest-inc-pet_card" /> Pet card <span className="text-xs text-q-muted">(photo, feeding, meds, quirks, vet, microchip)</span>
          </label>}
          {inc.pet_card && pets.length > 1 && pets.map((p) => (
            <label key={p.id} className="ml-9 flex min-h-[44px] items-center gap-3 text-sm">
              <Checkbox className="h-6 w-6" checked={!inc.pet_ids.length || inc.pet_ids.includes(p.id)} onCheckedChange={(v) => {
                const cur = inc.pet_ids.length ? inc.pet_ids : pets.map((x) => x.id);
                setInc({ ...inc, pet_ids: v ? [...new Set([...cur, p.id])] : cur.filter((x) => x !== p.id) });
              }} data-testid={`guest-pet-${p.id}`} /> {p.name}
            </label>))}
          {care.map((p) => (
            <label key={p.id} className="flex min-h-[44px] items-center gap-3">
              <Checkbox className="h-6 w-6" checked={inc.care_ids.includes(p.id)} onCheckedChange={(v) => setInc({ ...inc, care_ids: v ? [...inc.care_ids, p.id] : inc.care_ids.filter((x) => x !== p.id) })} data-testid={`guest-care-${p.id}`} /> Care emergency card: {p.name}
            </label>))}
        </div>
        <Field label="Lasts for"><select className="q-input w-full" value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="sitter-hours-select">{durations.map((d) => <option key={d.hours} value={d.hours}>{d.label}</option>)}</select></Field>
        <button className="q-btn q-btn-primary w-full" disabled={busy || !name.trim() || !any} onClick={make} data-testid="sitter-create-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Create pass</button>
      </DialogContent>
    </Dialog>
  );
}

const incLabel = (l) => [...(l.kids.length ? [l.kids.join(", ")] : []), ...INC.filter(([k]) => l.include?.[k]).map(([, x]) => x), ...(l.include?.care_ids?.length ? ["Care emergency cards"] : [])].join(" · ");

export default function SitterLinks({ hubId, kids, readOnly }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [making, setMaking] = useState(false);
  const [shown, setShown] = useState(null);
  const base = `/organizations/${hubId}/emergency/guest-passes`;
  const load = useCallback(() => api.get(base).then(({ data }) => setD(data)).catch(() => {}), [base]);
  useEffect(() => { load(); }, [load]);
  const detail = async (l) => { const { data } = await api.get(`${base}/${l.id}`); setOpen(data); };
  const act = async (path, msg) => {
    try { const { data } = await api.post(`${base}/${open.id}/${path}`); toast.success(msg); if (data.code) setShown({ name: open.name, code: data.code }); load(); detail(open); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return null;
  return (
    <section className="q-card space-y-3" data-testid="sitter-links">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex-1 font-display text-lg font-extrabold text-q-text">Guest passes</h2>
        <button className="q-btn q-btn-primary" disabled={readOnly} onClick={() => setMaking(true)} data-testid="sitter-link-btn"><Plus className="h-4 w-4" /> Guest pass</button>
      </div>
      {d.links.length === 0 && <p className="text-sm text-q-muted">No guest passes yet.</p>}
      {d.links.map((l) => (
        <button key={l.id} className="flex min-h-[52px] w-full items-center gap-3 rounded-xl border border-q-line px-3 py-2 text-left hover:bg-q-raised" onClick={() => detail(l)} data-testid={`sitter-row-${l.name.replace(/\s+/g, "-")}`}>
          <span className="min-w-0 flex-1"><span className="block truncate font-semibold text-q-text">{l.name}</span><span className="block text-xs text-q-faint"><b data-testid={`guest-type-${l.id}`}>{l.type}</b> · {incLabel(l)} · until {when(l.expires_at)} · {l.visit_count} visit{l.visit_count === 1 ? "" : "s"}</span></span>
          <StatusPill label={l.status} rgb={STATUS_RGB[l.status]} testid={`sitter-status-${l.id}`} />
        </button>
      ))}
      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(null)}>
          <DialogContent bigClose className="max-h-[92vh] overflow-y-auto sm:max-w-md" data-testid="sitter-detail">
            <DialogHeader><DialogTitle className="font-display">{open.name}</DialogTitle></DialogHeader>
            <p className="text-sm text-q-muted">{open.type} · {incLabel(open)} · {open.status} · until {when(open.expires_at)}</p>
            <div>
              <p className="mb-1 text-xs font-bold text-q-muted">Visits</p>
              {open.visits.length ? open.visits.map((v, n) => <p key={n} className="text-sm text-q-text" data-testid={`sitter-visit-${n}`}>{when(v.at)} · {v.device}</p>) : <p className="text-sm text-q-faint" data-testid="sitter-no-visits">No visits yet</p>}
            </div>
            {open.status === "Active" && (
              <div className="grid grid-cols-2 gap-2">
                <button className="q-btn q-btn-secondary" onClick={() => act("new-code", "New code made. Old devices are signed out.")} data-testid="sitter-new-code-btn"><KeyRound className="h-4 w-4" /> New code</button>
                <button className="q-btn q-btn-ghost" onClick={() => act("revoke", "Link revoked")} data-testid="sitter-revoke-btn"><ShieldOff className="h-4 w-4" /> Revoke</button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
      {making && <NewLink hubId={hubId} kids={kids} durations={d.durations} types={d.types} pets={d.pets} onClose={() => setMaking(false)} onMade={(s) => { setMaking(false); setShown(s); load(); }} />}
      {shown && <Secret shown={shown} onClose={() => setShown(null)} />}
    </section>
  );
}
